import { createSlice } from '@reduxjs/toolkit';
import { handleRejected } from '../../helpers';
import {
  getQuickbooksData,
  handleCustomerChoiceChange,
  handleCustomFieldChoiceChange,
  handleProductChoiceChange,
  postCustomerMappings,
  postCustomFieldMappings,
  postProductMappings,
  refreshQuickbooksIntegration,
  resetCustomerMappings,
  resetCustomFieldMappings,
  resetProductMappings,
  revokeQuickbooksIntegration,
  setActiveTab,
  setInitialQuickbooksData,
  setTableRowsPerPage,
} from '../actions/quickbooksActions';
import quickbooksState from '../states/quickbooksState';

export const quickbooksSlice = createSlice({
  name: 'quickbooks',
  initialState: quickbooksState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getQuickbooksData.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(getQuickbooksData.fulfilled, (state, action) => {
      const {
        quickbooksOnlineLTCustomers: { customers: ltCustomers },
        quickbooksOnlineQBOCustomers: { customers: qboCustomers },
        quickbooksOnlineProducts: { products },
        quickbooksOnlineItems: { items },
        quickbooksOnlineSalesformCustomFields: {
          salesFormCustomFields,
          choices,
        },
      } = action.payload;

      state.isLoading = false;
      state.quickbooksOnlineCustomers.customers = ltCustomers;
      state.quickbooksOnlineCustomers.initialCustomers = ltCustomers;
      state.quickbooksOnlineCustomers.customerChoices = qboCustomers;
      state.quickbooksOnlineProducts.products = products;
      state.quickbooksOnlineProducts.initialProducts = products;
      state.quickbooksOnlineProducts.productChoices = items;
      state.salesformCustomFields.customFields = salesFormCustomFields;
      state.salesformCustomFields.initialCustomFields = salesFormCustomFields;
      state.salesformCustomFields.customFieldChoices = choices;
    });

    builder.addCase(getQuickbooksData.rejected, (state, action) => {
      state.isLoading = false;
      state.formInitialDataValid = false;
      handleRejected(state, action);
    });

    builder.addCase(handleCustomerChoiceChange, (state, action) => {
      const { ltCustomerId, qboCustomerId } = action.payload;

      const newCustomers = state.quickbooksOnlineCustomers.customers.map(
        (customer) => {
          if (Number(customer.id) === Number(ltCustomerId)) {
            return {
              ...customer,
              foreignId: qboCustomerId || null,
            };
          }
          return customer;
        }
      );
      state.quickbooksOnlineCustomers.customers = newCustomers;
    });

    builder.addCase(handleCustomFieldChoiceChange, (state, action) => {
      const { ltCustomFieldId, qboCustomFieldId } = action.payload;

      const newCustomFields = state.salesformCustomFields.customFields.map(
        (customField) => {
          if (Number(customField.foreignId) === Number(ltCustomFieldId)) {
            return {
              ...customField,
              customFieldMappingId: qboCustomFieldId || null,
            };
          }
          return customField;
        }
      );
      state.salesformCustomFields.customFields = newCustomFields;
    });

    builder.addCase(handleProductChoiceChange, (state, action) => {
      const { ltProductId, qboProductId } = action.payload;

      const newProducts = state.quickbooksOnlineProducts.products.map(
        (product) => {
          if (Number(product.id) === Number(ltProductId)) {
            return {
              ...product,
              foreignId: qboProductId || null,
            };
          }
          return product;
        }
      );
      state.quickbooksOnlineProducts.products = newProducts;
    });

    builder.addCase(postCustomerMappings.pending, (state) => {
      state.quickbooksOnlineCustomers.isSavingCustomerMappings = true;
    });

    builder.addCase(postCustomerMappings.fulfilled, (state, action) => {
      const { customers } = action.payload;
      state.quickbooksOnlineCustomers.isSavingCustomerMappings = false;
      state.quickbooksOnlineCustomers.customers = customers;
      state.quickbooksOnlineCustomers.initialCustomers = customers;
    });

    builder.addCase(postCustomerMappings.rejected, (state, action) => {
      state.quickbooksOnlineCustomers.isSavingCustomerMappings = false;
      handleRejected(state, action);
    });

    builder.addCase(postCustomFieldMappings.pending, (state) => {
      state.salesformCustomFields.isSavingCustomFields = true;
    });

    builder.addCase(postCustomFieldMappings.fulfilled, (state, action) => {
      const { salesFormCustomFields } = action.payload;
      state.salesformCustomFields.isSavingCustomFields = false;
      state.salesformCustomFields.customFields = salesFormCustomFields;
      state.salesformCustomFields.initialCustomFields = salesFormCustomFields;
    });

    builder.addCase(postCustomFieldMappings.rejected, (state, action) => {
      state.salesformCustomFields.isSavingCustomFields = false;
      handleRejected(state, action);
    });

    builder.addCase(postProductMappings.pending, (state) => {
      state.quickbooksOnlineProducts.isSavingProductMappings = true;
    });

    builder.addCase(postProductMappings.fulfilled, (state, action) => {
      const { products } = action.payload;
      state.quickbooksOnlineProducts.isSavingProductMappings = false;
      state.quickbooksOnlineProducts.products = products;
      state.quickbooksOnlineProducts.initialProducts = products;
    });

    builder.addCase(postProductMappings.rejected, (state, action) => {
      state.quickbooksOnlineProducts.isSavingProductMappings = false;
      handleRejected(state, action);
    });

    builder.addCase(refreshQuickbooksIntegration.pending, (state) => {
      state.isRefreshing = true;
    });

    builder.addCase(refreshQuickbooksIntegration.fulfilled, (state) => {
      state.isRefreshing = false;
    });

    builder.addCase(refreshQuickbooksIntegration.rejected, (state, action) => {
      state.isRefreshing = false;
      handleRejected(state, action);
    });

    builder.addCase(resetCustomerMappings, (state) => {
      state.quickbooksOnlineCustomers.customers =
        state.quickbooksOnlineCustomers.initialCustomers;
    });

    builder.addCase(resetCustomFieldMappings, (state) => {
      state.salesformCustomFields.customFields =
        state.salesformCustomFields.initialCustomFields;
    });

    builder.addCase(resetProductMappings, (state) => {
      state.quickbooksOnlineProducts.products =
        state.quickbooksOnlineProducts.initialProducts;
    });

    builder.addCase(revokeQuickbooksIntegration.pending, (state) => {
      state.isRevoking = true;
    });

    builder.addCase(revokeQuickbooksIntegration.fulfilled, (state) => {
      state.isRevoking = false;
      state.quickbooksOnlineIntegrated = false;
    });

    builder.addCase(revokeQuickbooksIntegration.rejected, (state, action) => {
      state.isRevoking = false;
      handleRejected(state, action);
    });

    builder.addCase(setActiveTab, (state, action) => {
      state.activeTab = action.payload;
    });

    builder.addCase(setInitialQuickbooksData, (state, action) => {
      const {
        beginOAuthUrl,
        hasEditPermission,
        purgeCacheUrl,
        quickbooksOnlineIntegrated,
        qboCompanyName,
        revokeUrl,
        vendorId,
      } = action.payload;

      Object.assign(state, {
        beginOAuthUrl,
        hasEditPermission,
        purgeCacheUrl,
        quickbooksOnlineIntegrated,
        qboCompanyName,
        revokeUrl,
        vendorId,
      });
    });

    builder.addCase(setTableRowsPerPage, (state, action) => {
      state.tableRowsPerPage.rowsPerPage = action.payload;
    });
  },
});
